import type { FC } from 'react';

import { useMutation } from '@tanstack/react-query';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useAppState } from '../../../../AppStateProvider';
import { Tooltip } from '../../../../components/Tooltip';
import { hasDataToReveal } from '../../../../domain/contact';
import type { Contact, PrivateData } from '../../../../domain/contact';
import { useUserStatus } from '../../../../hooks/useUserStatus';
import { appStore } from '../../../../services/appStore';
import { translate } from '../../../../services/i18n';
import { useUseCases } from '../../../../UseCaseProvider';
import { RevealButton } from '../RevealButton';

import { StyledContactNoDetails } from './styles';
import { useContactRevealTracking } from './useContactRevealTracking';

export type RevealContainerProps = {
  contact: Contact;
  isQuotaDepleted: boolean;
  setShouldAnimatePrivateDataMount: (shouldAnimate: boolean) => void;
};

export const RevealContainer: FC<RevealContainerProps> = ({
  contact,
  setShouldAnimatePrivateDataMount,
  isQuotaDepleted,
}) => {
  const { revealContact } = useUseCases();
  const { notRevealedContactsIds } = useAppState();
  const { handleTrackRevealClick, handleTrackRevealFinish } = useContactRevealTracking();
  const { isTrialOrAfterTrial } = useUserStatus();

  const onRevealFinish = ({ isSuccessful, privateData }: { isSuccessful: boolean; privateData?: PrivateData }) => {
    handleTrackRevealFinish({ isSuccessful });
    setShouldAnimatePrivateDataMount(true);

    if (isSuccessful && privateData) {
      appStore.makeSelectedContactRevealed(contact, privateData);
    }
  };

  const { isPending, mutate } = useMutation({
    mutationFn: revealContact,
    onSuccess: onRevealFinish,
    onError: () => handleTrackRevealFinish({ isSuccessful: false }),
  });

  const handleRevealClick = () => {
    mutate(contact.id);
    handleTrackRevealClick(contact);
  };

  const isRevealError = notRevealedContactsIds.includes(contact.id);
  const isRevealAvailable = hasDataToReveal(contact);

  if (isRevealError) {
    return (
      <Tooltip content={translate('contact_card.reveal_failed')} placement={'left'} minWidth={160}>
        <SWReactIcons iconColor={colorsPalettes.red['400']} iconName="alert-circle" size="xs" />
      </Tooltip>
    );
  }
  if (!isRevealAvailable) {
    return (
      <StyledContactNoDetails data-automation="RevealNoDetails">
        {translate('contact_card.no_details')}
      </StyledContactNoDetails>
    );
  }

  if (!contact.isRevealed && (!isTrialOrAfterTrial || (isTrialOrAfterTrial && !isQuotaDepleted))) {
    return <RevealButton onClick={handleRevealClick} isLoading={isPending} />;
  }

  return null;
};
